import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import { EntityLinkType } from 'src/services/common.types';
import { ContactSource } from 'src/services/contacts/contacts.types';
import { ICompanyContactLinkListItem } from 'src/services/connections/connections.types';

import Label from 'src/components/label';
import Iconify from 'src/components/iconify';
import { ColumnDef } from 'src/components/data-list/data-list.types';

import { buildContactColumns } from 'src/sections/contacts/table/contact-columns';

// ----------------------------------------------------------------------

type Translate = (key: string, options?: any) => string;

const dash = <Typography variant="body2" color="textSecondary">--</Typography>;

/**
 * Columns the contacts registry provides that make no sense inside a company profile: the linked
 * company is the record you are already looking at, and list/label membership belongs to the
 * contact rather than this relationship.
 */
const OMITTED_CONTACT_COLUMNS = new Set(['company', 'lists', 'labels']);

/** Which contacts columns lead the mobile card, so the cards match the main contacts list. */
const CARD_ROLES: Record<string, ColumnDef<any>['cardRole']> = {
  name: 'primary',
  position_title: 'secondary',
};

interface BuildContactLinkColumnsArgs {
  t: Translate;
  onOpenRecord: (contactId: string) => void;
}

/**
 * Linked-contacts registry for the company profile's Contacts tab.
 *
 * Reuses the contacts registry rather than restating it, so the two tables cannot drift: a column
 * added to the contacts list appears here too. The row type extends IContactListItem precisely so
 * this spread type-checks — see ICompanyContactLinkListItem.
 *
 * Appended here are the four fields that only exist in the context of a link: how the contact is
 * attached to this company, what kind of contact they are, where they came from, and who they
 * report to.
 */
export function buildContactLinkColumns({
  t,
  onOpenRecord,
}: BuildContactLinkColumnsArgs): ColumnDef<ICompanyContactLinkListItem>[] {
  const inherited = buildContactColumns({ t, onOpenRecord })
    .filter((col) => !OMITTED_CONTACT_COLUMNS.has(col.key))
    .map((col) => ({ ...col, cardRole: CARD_ROLES[col.key] }));

  const linkColumns: ColumnDef<ICompanyContactLinkListItem>[] = [
    {
      key: 'link_type',
      label: t('companies.profile.linked_contacts.link_type', { defaultValue: 'Link' }),
      defaultVisible: true,
      sortable: true,
      cardRole: 'meta',
      render: (row) =>
        row.link_type === EntityLinkType.PRIMARY ? (
          <Tooltip title={t('companies.profile.primary_contact.tooltip')}>
            <Stack direction="row" alignItems="center" gap={0.5}>
              <Iconify icon="solar:star-bold" sx={{ color: 'primary.main' }} width={16} />
              <Typography variant="body2">
                {t('companies.profile.linked_contacts.primary', { defaultValue: 'Primary' })}
              </Typography>
            </Stack>
          </Tooltip>
        ) : dash,
    },
    {
      key: 'contact_type',
      label: t('contacts.table.type', { defaultValue: 'Contact type' }),
      defaultVisible: true,
      sortable: true,
      cardRole: 'meta',
      render: (row) =>
        row.contact_type ? (
          <Label variant="soft" color="default">{t(`enums.contact_type.${row.contact_type}`)}</Label>
        ) : dash,
    },
    {
      key: 'source',
      label: t('contacts.table.source', { defaultValue: 'Source' }),
      defaultVisible: false,
      sortable: true,
      cardRole: 'meta',
      render: (row) => {
        if (!row.source) return dash;
        return (
          <Tooltip
            title={
              row.source === ContactSource.JOB_INTERACTION
                ? t('contacts.source.tooltip_job_interaction')
                : t('contacts.source.tooltip_manual')
            }
          >
            <Label variant="soft" color={row.source === ContactSource.JOB_INTERACTION ? 'success' : 'info'}>
              {t(`enums.contact_source.${row.source}`)}
            </Label>
          </Tooltip>
        );
      },
    },
    {
      key: 'reports_to',
      label: t('contacts.table.reports_to', { defaultValue: 'Reports to' }),
      defaultVisible: false,
      searchable: true,
      render: (row) =>
        row.reports_to_name ? (
          <Typography variant="body2" color="text.secondary">{row.reports_to_name}</Typography>
        ) : dash,
    },
  ];

  // Link columns sit directly after the name so the primary-contact star reads as part of the
  // identity, not as a trailing afterthought.
  const [nameColumn, ...rest] = inherited;
  return [nameColumn, ...linkColumns, ...rest];
}
